/**
 * Pure date-bucketing logic for the staff-task board. No React — unit-testable.
 */

export const DONE = 'done';
export const OPEN = 'open';
export const CANCELLED = 'cancelled';
export const IN_PROGRESS = 'in_progress';

export const PRIORITIES = ['low', 'normal', 'high'] as const;

/** Local-time `YYYY-MM-DD`. */
const pad = (n: number) => String(n).padStart(2, '0');
const fmt = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const localToday = (): string => fmt(new Date());
export const addDays = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return fmt(new Date(y, m - 1, d + n));
};

export type Bucket =
  | 'overdue'
  | 'today'
  | 'tomorrow'
  | 'thisWeek'
  | 'later'
  | 'noDate'
  | 'done';

export interface IStaffTask {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  dueDate: string | null;
  /** Manual board rank (drag-and-drop). */
  position?: number | null;
  /** Spun off from a conversation message. */
  fromMessage?: boolean | null;
  completedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  assigneeName?: string | null;
  createdByName?: string | null;
  group?: { id: string; name: string } | null;
  student?: {
    id: string;
    user?: { firstName?: string | null; lastName?: string | null } | null;
  } | null;
  agreement?: { id: string; agreementTitle: string | null } | null;
}

/** Payload for creating a task (inline / modal). `status` seeds the column a
 *  «+» button was clicked in (status grouping); defaults to open server-side. */
export interface ICreateTaskDraft {
  title: string;
  description?: string;
  groupId?: string;
  studentId?: string;
  agreementId?: string;
  assigneeId?: string;
  dueDate?: string;
  priority: string;
  status?: string;
}

export const isClosed = (task: IStaffTask): boolean =>
  task.status === DONE || task.status === CANCELLED;

export const studentName = (task: IStaffTask): string =>
  task.student
    ? [task.student.user?.lastName, task.student.user?.firstName]
        .filter(Boolean)
        .join(' ')
    : '';

export const bucketOf = (
  task: IStaffTask,
  today: string,
  tomorrow: string,
  weekEnd: string,
): Bucket => {
  if (isClosed(task)) return 'done';
  const d = task.dueDate;
  if (!d) return 'noDate';
  if (d < today) return 'overdue';
  if (d === today) return 'today';
  if (d === tomorrow) return 'tomorrow';
  if (d <= weekEnd) return 'thisWeek';
  return 'later';
};

export type TBucketMap = Record<Bucket, IStaffTask[]>;

export const emptyBuckets = (): TBucketMap => ({
  overdue: [],
  today: [],
  tomorrow: [],
  thisWeek: [],
  later: [],
  noDate: [],
  done: [],
});

export const bucketTasks = (tasks: IStaffTask[], today: string): TBucketMap => {
  const tomorrow = addDays(today, 1);
  const weekEnd = addDays(today, 7);
  const map = emptyBuckets();
  tasks.forEach((task) => {
    map[bucketOf(task, today, tomorrow, weekEnd)].push(task);
  });
  return map;
};

export type TColumnTone = 'danger' | 'warn';

/**
 * The five always-visible date columns. `noDate` tasks ride in «Позже» (a 6th
 * column would starve the others on laptop widths); done/cancelled live in a
 * collapsible strip below. `overdue` is not a drag drop-target — you cannot
 * reschedule a task into the past.
 */
export const COLUMNS: Array<{ key: Bucket; tone?: TColumnTone }> = [
  { key: 'overdue', tone: 'danger' },
  { key: 'today', tone: 'warn' },
  { key: 'tomorrow' },
  { key: 'thisWeek' },
  { key: 'later' },
];

/* --- Status grouping (kanban): open / in progress / done -------------------- */

export type StatusKey = 'open' | 'in_progress' | 'done';

export const STATUS_COLUMNS: Array<{ key: StatusKey; tone?: TColumnTone }> = [
  { key: 'open' },
  { key: 'in_progress', tone: 'warn' },
  { key: 'done' },
];

export type TStatusMap = Record<StatusKey, IStaffTask[]>;

/** Bucket tasks by status column (cancelled shows alongside done). */
export const bucketByStatus = (tasks: IStaffTask[]): TStatusMap => {
  const map: TStatusMap = { open: [], in_progress: [], done: [] };
  tasks.forEach((task) => {
    if (task.status === IN_PROGRESS) map.in_progress.push(task);
    else if (isClosed(task)) map.done.push(task);
    else map.open.push(task);
  });
  return map;
};

/** Patch applied when a card is dropped into a DEADLINE column: reschedule it
 *  to a concrete date. `thisWeek`/`later` keep an already-in-range date. */
export interface IDeadlineDrop {
  targetDueDate?: string;
}

export const deadlineDropPatch = (
  bucket: Bucket,
  today: string,
  currentDue: string | null,
): IDeadlineDrop | null => {
  const tomorrow = addDays(today, 1);
  const weekEnd = addDays(today, 7);
  switch (bucket) {
    case 'today':
      return { targetDueDate: today };
    case 'tomorrow':
      return { targetDueDate: tomorrow };
    case 'thisWeek':
      return currentDue && currentDue > tomorrow && currentDue <= weekEnd
        ? {}
        : { targetDueDate: addDays(today, 2) };
    case 'later':
      return currentDue && currentDue > weekEnd
        ? {}
        : { targetDueDate: addDays(today, 8) };
    default:
      return null; // overdue / noDate / done — not a drop target
  }
};
